// Centralized Gmail/Outlook provider resolution — single source of truth for the
// aliases a channel's persisted provider value may take (e.g. Office 365 OAuth
// persists 'microsoft' rather than 'office365'; see AddSpaceModal.tsx oauth_provider mapping).

export type WebmailProvider = 'gmail' | 'outlook';

const OUTLOOK_PROVIDER_ALIASES = new Set(['office365', 'outlook', 'microsoft']);

export interface WebmailProviderInput {
  provider?: string | null;
  channel_settings?: { provider?: string } | null;
  messaging_provider?: string | null;
}

/**
 * Resolves a channel's raw provider fields (top-level provider, channel_settings.provider,
 * messaging_provider) down to a known webmail provider, or null if not Gmail/Outlook.
 */
export const resolveWebmailProvider = (
  channel: WebmailProviderInput | null | undefined
): WebmailProvider | null => {
  const raw =
    channel?.provider ??
    channel?.channel_settings?.provider ??
    channel?.messaging_provider ??
    '';
  const value = raw.toLowerCase();
  if (value === 'gmail') return 'gmail';
  if (OUTLOOK_PROVIDER_ALIASES.has(value)) return 'outlook';
  return null;
};

/**
 * Returns true when a channel's provider resolves to Google Chat.
 * Checks the same three API shapes as resolveWebmailProvider so both helpers
 * stay in sync: top-level provider, channel_settings.provider, messaging_provider.
 * By the time channelData reaches the component, ThreadDetailPage normalises all
 * three shapes into messaging_provider — the fallbacks exist for any caller that
 * passes a raw (un-normalised) API response.
 */
export const isGoogleChatChannel = (
  channel: WebmailProviderInput | null | undefined
): boolean => {
  const raw =
    channel?.provider ??
    channel?.channel_settings?.provider ??
    channel?.messaging_provider ??
    '';
  return raw.toLowerCase() === 'google_chat';
};

export const WEBMAIL_PROVIDER_CONFIG: Record<
  WebmailProvider,
  { url: string; label: string; iconSrc: string }
> = {
  gmail: {
    url: 'https://mail.google.com',
    label: 'Gmail',
    iconSrc: '/images/apps/gmail.png',
  },
  outlook: {
    url: 'https://outlook.office365.com',
    label: 'Outlook',
    iconSrc: '/images/apps/outlook-v2.png',
  },
};

export type MessagingProvider = 'regular' | 'whatsapp' | 'slack' | 'google_chat' | 'teams';

/**
 * Resolves a message channel's raw provider fields down to a known messaging provider,
 * defaulting to 'regular' for anything unset/unrecognized (including 'google_chat_inbox',
 * folded into 'google_chat' here) — mirrors resolveWebmailProvider/isGoogleChatChannel's
 * same three-shape lookup (top-level provider, channel_settings.provider, messaging_provider)
 * so all three provider-resolution helpers in this file stay in sync.
 */
export const resolveMessagingProvider = (
  channel: WebmailProviderInput | null | undefined
): MessagingProvider => {
  const raw =
    channel?.provider ??
    channel?.channel_settings?.provider ??
    channel?.messaging_provider ??
    '';
  const value = raw.toLowerCase();
  if (value === 'whatsapp') return 'whatsapp';
  if (value === 'slack') return 'slack';
  if (value === 'google_chat' || value === 'google_chat_inbox') return 'google_chat';
  if (value === 'teams') return 'teams';
  return 'regular';
};

/**
 * icon/url/label per messaging provider for the "What would you like to do?" shortcut
 * card — 'regular' has no external app to open, so its url is null (card renders with
 * its icon but no click-through, per that provider's own in-app chat UI).
 */
export const MESSAGING_PROVIDER_CONFIG: Record<
  MessagingProvider,
  { url: string | null; label: string; iconSrc: string | null }
> = {
  regular: {
    url: null,
    label: 'Regular',
    iconSrc: null, // no PNG asset for Regular — component falls back to the MUI ChatIcon
  },
  whatsapp: {
    url: 'https://web.whatsapp.com',
    label: 'WhatsApp',
    iconSrc: '/images/apps/whatsapp.png',
  },
  slack: {
    url: 'https://slack.com',
    label: 'Slack',
    iconSrc: '/images/apps/slack.png',
  },
  google_chat: {
    url: 'https://chat.google.com',
    label: 'Google Chat',
    iconSrc: '/images/apps/gchat.png',
  },
  teams: {
    url: 'https://teams.microsoft.com',
    label: 'Teams',
    iconSrc: '/images/apps/teams.png',
  },
};
